import { has } from 'lodash';
import { getOrgAccountMetrics, getUserAccountMetrics } from 'js/reducers/metrics';
import {
  METRICS_RESOURCE_NAME_SINK,
  METRICS_RESOURCE_NAME_SOURCE,
} from 'js/constants/resourceTypes';
import { useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { fetchOrgResourceMetricsPerDay, fetchUserResourceMetricsPerDay } from 'js/actions';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import moment from 'moment';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { ViewType } from 'new/components/monitor/Utilization/constants/viewTypes';

export function useUserResourceUsageMetrics(
  resourceType: 'sink' | 'source' | 'summary',
  viewType: ViewType,
) {
  const org = useCurrentOrg();
  const user = useCurrentUser();
  const dispatch = useAppDispatch();

  const request = useSelector(
    (state: { request: Record<string, boolean | undefined> }) => state.request,
  );
  const accountMetrics = useSelector(
    viewType === ViewType.Org ? getOrgAccountMetrics : getUserAccountMetrics,
  );

  const fetch = useCallback(
    (dates) => {
      // Metrics are bucketed by UTC day; format in UTC so the queried range
      // matches the UTC ticks the chart renders (local time would shift a day
      // for non-UTC zones and leave the latest day unfetched).
      const formattedDates = {
        start: moment.utc(dates.start).format('YYYY-MM-DD'),
        end: moment.utc(dates.end).format('YYYY-MM-DD'),
      };
      if (viewType === ViewType.Org) {
        dispatch(fetchOrgResourceMetricsPerDay(org.id, resourceType.toUpperCase(), formattedDates));
      } else {
        dispatch(
          fetchUserResourceMetricsPerDay(
            user.id,
            org.id,
            resourceType.toUpperCase(),
            formattedDates,
          ),
        );
      }
    },
    [resourceType, viewType, org.id, user.id, dispatch],
  );

  return useMemo(() => {
    const loadingByResource: Record<'summary' | 'source' | 'sink', boolean> = {
      summary:
        request.GET_USER_ACCOUNT_SUMMARY_REQUEST ||
        request.GET_ORG_ACCOUNT_SUMMARY_REQUEST ||
        false,
      source:
        request.GET_USER_SOURCE_METRICS_N_DAYS_REQUEST ||
        request.GET_ORG_SOURCE_METRICS_N_DAYS_REQUEST ||
        false,
      sink:
        request.GET_USER_SINK_METRICS_N_DAYS_REQUEST ||
        request.GET_ORG_SINK_METRICS_N_DAYS_REQUEST ||
        false,
    };

    const dataByResource = {
      summary: accountMetrics ? accountMetrics.summary.metrics : null,
      source:
        accountMetrics && has(accountMetrics, METRICS_RESOURCE_NAME_SOURCE)
          ? accountMetrics[METRICS_RESOURCE_NAME_SOURCE].byDate
          : null,
      sink:
        accountMetrics && has(accountMetrics, METRICS_RESOURCE_NAME_SINK)
          ? accountMetrics[METRICS_RESOURCE_NAME_SINK].byDate
          : null,
    };

    return {
      loading: loadingByResource[resourceType],
      data: dataByResource[resourceType],
      fetch,
    };
  }, [resourceType, request, accountMetrics, fetch]);
}
